import { useEffect, useMemo, useState } from 'react'
import type { IntakeDomain } from '../../orders/api/intakeApi'
import { useSolutionCatalog, useSaveSolutionCatalog } from '../hooks/useFieldConfig'
import { validateFieldDefinitions } from '../utils/validateFieldDefinitions'
import { fromWorkingFields, toWorkingFields, type WorkingField } from '../utils/workingFields'
import { FieldSetEditor } from './FieldSetEditor'
import { FieldPreview } from './FieldPreview'
import { Button } from '../../../components/ui/button'
import { Badge } from '../../../components/ui/badge'
import { Save, Eye, EyeOff } from 'lucide-react'

interface SolutionCatalogEditorProps {
  solutionId: string
  domain: IntakeDomain
}

/**
 * The full field-authoring editor bound to the SOLUTION TEMPLATE (TCS-admin).
 * Loads GET /field-config/solution/:domain, saves PUT { fields }.
 *
 * Under the template-copy model these fields are the DEFAULT: merchants without their
 * own configuration use them verbatim, and "Customize for this merchant" starts from a
 * copy of this list. Editing the template does NOT change merchants that already
 * customized — their copies are independent.
 */
export function SolutionCatalogEditor({ solutionId, domain }: SolutionCatalogEditorProps) {
  const { data, isLoading, error } = useSolutionCatalog(solutionId, domain)
  const saveMut = useSaveSolutionCatalog(solutionId, domain)

  const [fields, setFields] = useState<WorkingField[]>([])
  const [dirty, setDirty] = useState(false)
  const [showPreview, setShowPreview] = useState(false)

  // Sync from server when data loads (but not when the user has unsaved changes).
  useEffect(() => {
    if (data && !dirty) setFields(toWorkingFields(data.fields))
  }, [data, dirty])

  const validationErrors = useMemo(() => validateFieldDefinitions(fields), [fields])
  const canSave = dirty && validationErrors.length === 0 && !saveMut.isPending

  const handleChange = (next: WorkingField[]) => {
    setFields(next)
    setDirty(true)
  }

  const handleSave = () => {
    if (!canSave) return
    saveMut.mutate(fromWorkingFields(fields), { onSuccess: () => setDirty(false) })
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-sm text-muted-foreground">Loading solution template…</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4">
        <p className="text-sm text-red-700">
          Failed to load the solution template: {(error as { message?: string }).message ?? 'Unknown error'}
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-foreground">Solution template fields</h3>
          <Badge variant="secondary" className="text-[10px]">{solutionId}</Badge>
          {dirty && (
            <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700">Unsaved</Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => setShowPreview((v) => !v)}>
            {showPreview ? <EyeOff className="h-4 w-4 mr-1" /> : <Eye className="h-4 w-4 mr-1" />}
            {showPreview ? 'Hide Preview' : 'Preview'}
          </Button>
          <Button size="sm" onClick={handleSave} disabled={!canSave}>
            <Save className="h-4 w-4 mr-1" />
            {saveMut.isPending ? 'Saving…' : 'Save template'}
          </Button>
        </div>
      </div>

      <p className="text-xs text-muted-foreground">
        These are the default (template) fields for this solution. Merchants without their own
        configuration use them verbatim; customizing a merchant copies this template as a starting
        point. Editing the template does not change merchants that already customized.
      </p>

      {dirty && validationErrors.length > 0 && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
          <p className="text-xs font-medium text-amber-800 mb-1">Fix before saving:</p>
          <ul className="list-disc list-inside text-xs text-amber-700 space-y-0.5">
            {validationErrors.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        </div>
      )}

      {saveMut.isError && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3">
          <p className="text-xs text-red-700">
            Save failed: {(saveMut.error as { message?: string }).message ?? 'Unknown error'}.
          </p>
        </div>
      )}

      <FieldSetEditor
        fields={fields}
        onChange={handleChange}
        emptyLabel="No template fields yet for this domain."
        addLabel="Add template field"
      />

      {showPreview && fields.length > 0 && <FieldPreview fields={fromWorkingFields(fields)} />}
    </div>
  )
}
